import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CertificateRecord } from 'app/library/certificate-record/models/certificate-record';
import { CertificateRecordApiService } from 'app/library/certificate-record/services/certificate-record.api-service';
import { CertificateRecordService } from 'app/library/certificate-record/services/certificate-record.service';
import { ReplaceCertificateDialogComponent } from 'app/library/certification-provider/components/dialogs/replace-certificate-dialog/replace-certificate-dialog.component';
import { CertificationFlatNode } from 'app/library/certification-provider/components/providers-all-list/providers-all-list.component';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { CertificationProviderService } from 'app/library/certification-provider/services/certification-provider.service';
import { Certification } from 'app/library/certifications/models/certification';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { Instance } from '@nutrient-sdk/viewer';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-edit-certificate-dialog',
    templateUrl: './edit-certificate-dialog.component.html',
    styleUrls: ['./edit-certificate-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class EditCertificateDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    title: string;

    @Input()
    certificateRecord: CertificateRecord;

    @Input()
    isViewOnly: boolean = false;

    selectedCertificationProviderId: string;
    selectedCertificationId: string;
    selectedCertification: Certification;

    isToDoFeatureEnabled: boolean = false;
    certificationForm: FormGroup;
    fileURL: string = '';
    doesCertificateFileExist: boolean = false;
    uploadedCertificateFiles: File[] = [];
    selectedCertificateToReplace: CertificationFlatNode;
    selectedCertificateToBeReplacedId: string;
    isFileUpdated: boolean = false;
    minDate: Date;
    hasEndDate = new FormControl(false);
    isDragOver = false;

    isSaveButtonLoading: boolean = false;
    isEndDateToggleChanged: boolean = false;

    Validators = Validators;

    private _instance: Instance;

    private _setToolbarItems(instance = this._instance): void {
        if (!instance) {
            return;
        }

        instance.setToolbarItems((items) => {
            items = items.filter((item) => item.type === 'zoom-in' || item.type === 'zoom-out');

            return items;
        });
    }

    constructor(
        public dialogRef: MatDialogRef<EditCertificateDialogComponent>,
        private _certificationProviderService: CertificationProviderService,
        private _certificateRecordService: CertificateRecordService,
        private _certificateRecordApiService: CertificateRecordApiService,
        private _featureService: FeatureService,
        private _fb: FormBuilder,
        private _matDialog: MatDialog
    ) {
        super();
    }

    ngOnInit(): void {
        this.isToDoFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.ToDo);
        this.selectedCertification = this.certificateRecord.officialCertificateMetadata.certification;
        this.selectedCertificationId = this.selectedCertification.id;
        this.selectedCertificationProviderId = this.selectedCertification.provider.id;

        this.certificationForm = this._fb.group({
            provider: [{ value: this.certificateRecord.officialCertificateMetadata.certification.provider.id, disabled: this.isViewOnly }, Validators.required],
            certification: [{ value: this.certificateRecord.officialCertificateMetadata.certification.id, disabled: this.isViewOnly }, Validators.required],
            issueDate: [{ value: this.certificateRecord.officialCertificateMetadata.issueDate, disabled: this.isViewOnly }, Validators.required],
        });

        if (
            this.certificateRecord.officialCertificateMetadata.certification.hasExpirationDate ||
            (this.certificateRecord.officialCertificateMetadata.certification.certificationName === 'other' && this.certificateRecord.officialCertificateMetadata.expirationDate)
        ) {
            this.certificationForm.addControl(
                'expirationDate',
                new FormControl({ value: this.certificateRecord.officialCertificateMetadata.expirationDate, disabled: this.isViewOnly }, Validators.required)
            );

            if (
                this.certificateRecord.officialCertificateMetadata.certification.certificationName === 'other' &&
                this.certificateRecord.officialCertificateMetadata.expirationDate
            ) {
                this.hasEndDate.setValue(true);
            }
        }

        if (
            this.isToDoFeatureEnabled &&
            (this.certificateRecord.officialCertificateMetadata.certification.hasExpirationDate ||
                (this.certificateRecord.officialCertificateMetadata.certification.certificationName === 'other' &&
                    this.certificateRecord.officialCertificateMetadata.expirationDate))
        ) {
            this.certificationForm.addControl(
                'notifyAboutExpiration',
                new FormControl({ value: this.certificateRecord.officialCertificateMetadata.notifyAboutExpiration, disabled: this.isViewOnly })
            );
        }

        this.updateExpirationDate();

        this._certificateRecordApiService
            .getCertificateFile(this.certificateRecord.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                (value) => {
                    this.fileURL = URL.createObjectURL(value);
                },
                async (error) => {}
            );
    }

    updateExpirationDate(): void {
        const startDate = this.certificationForm.get('issueDate').value;

        this.minDate = startDate ? new Date(startDate) : new Date();
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }
    onFileDropped(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;

        if (this.isViewOnly) {
            return;
        }

        if (event.dataTransfer && event.dataTransfer.files.length > 0) {
            const files: File[] = Array.from(event.dataTransfer.files);
            this.handleFiles(files);
        }
    }

    handleFiles(files: File[]): void {
        const fileSubject = new BehaviorSubject<File | null>(null);
        const collectedFiles: File[] = [];

        fileSubject
            .asObservable()
            .pipe(
                filter((file) => !!file),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((file) => {
                collectedFiles.push(file!);
                if (collectedFiles.length === files.length) {
                    fileSubject.complete();
                    this.fileChange(collectedFiles);
                }
            });

        files.forEach((file) => fileSubject.next(file));
    }

    fileChange(files: FileList | File[]): void {
        this.isFileUpdated = true;
        if (files instanceof FileList) {
            const temp: File[] = [];
            for (const key of Object.keys(files)) {
                temp[key] = files[key];
            }
            files = temp;
        }

        this.uploadedCertificateFiles = files;
    }

    downloadCertificate(): void {
        if (this.isViewOnly) {
            return;
        }

        this._certificateRecordService.downloadCertificate(this.certificateRecord.id);
    }

    onRemoveAddedCertificate(): void {
        this.uploadedCertificateFiles = [];
    }

    onRemoveReplacementCertificate(): void {
        this.selectedCertificateToReplace = null;
    }

    load(instance: Instance): void {
        this._setToolbarItems(instance);

        this._instance = instance;
    }

    async openReplaceCertificateDialog(certificate: CertificationFlatNode): Promise<void> {
        const dialogRef = this._matDialog.open(ReplaceCertificateDialogComponent, {
            width: '976px',
        });

        dialogRef.componentInstance.certificateToBeReplaced = certificate;
        dialogRef.componentInstance.buttonText = 'GENERAL.BUTTON.CONFIRM';

        dialogRef.afterClosed().subscribe(async (result: { newSelectedCertificate: CertificationFlatNode }) => {
            if (result) {
                this.selectedCertificateToReplace = result.newSelectedCertificate;
                this.selectedCertificateToBeReplacedId = certificate.id;

                this.certificationForm.markAsDirty();
            }
        });
    }

    onProviderSelected(provider: CertificationProvider): void {
        this.selectedCertificationProviderId = provider.id;
        this.selectedCertificationId = null;
        this.selectedCertification = null;
        this.hasEndDate.setValue(false);
        this.certificationForm.removeControl('expirationDate');
        if (this.isToDoFeatureEnabled) {
            this.certificationForm.removeControl('notifyAboutExpiration');
        }
    }

    onCertificationSelected(certification: Certification): void {
        this.selectedCertificationId = certification.id;
        this.selectedCertification = certification;

        if (this.selectedCertification.certificationName === 'other') {
            this.hasEndDate.setValue(true);
        } else {
            this.hasEndDate.setValue(false);
        }

        if (this.selectedCertification.hasExpirationDate || this.selectedCertification.certificationName === 'other') {
            if (!this.certificationForm.get('expirationDate')) {
                this.certificationForm.addControl(
                    'expirationDate',
                    new FormControl({ value: this.certificateRecord.officialCertificateMetadata.expirationDate, disabled: this.isViewOnly }, Validators.required)
                );
            }
            if (this.isToDoFeatureEnabled && !this.certificationForm.get('notifyAboutExpiration')) {
                this.certificationForm.addControl(
                    'notifyAboutExpiration',
                    new FormControl({ value: true, disabled: this.isViewOnly })
                );
            }
        } else {
            this.certificationForm.removeControl('expirationDate');
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.removeControl('notifyAboutExpiration');
            }
        }
    }

    onChangeEndDateToggle(event: MatCheckboxChange): void {
        if (event.checked) {
            this.isEndDateToggleChanged = false;
            this.certificationForm.addControl(
                'expirationDate',
                new FormControl({ value: null, disabled: this.isViewOnly }, Validators.required)
            );
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.addControl(
                    'notifyAboutExpiration',
                    new FormControl({ value: false, disabled: this.isViewOnly })
                );
            }
        } else {
            this.isEndDateToggleChanged = true;
            this.certificationForm.removeControl('expirationDate');
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.removeControl('notifyAboutExpiration');
            }
        }
    }

    async saveSelected(): Promise<void> {
        this.isSaveButtonLoading = true;

        this.isSaveButtonLoading = false;

        const success = await this._certificateRecordService.updateCertificateRecord(
            this.certificateRecord.id,
            this.certificationForm.get('certification').value,
            this.certificationForm.get('issueDate').value,
            this.certificationForm.get('expirationDate')?.value,
            this.certificationForm.get('notifyAboutExpiration')?.value
        );

        let replaceSuccess: boolean;
        let fileUploadSuccess: CertificateRecord;

        if (this.selectedCertificateToReplace) {
            replaceSuccess = await this._certificationProviderService.replaceCertificate(this.selectedCertificateToBeReplacedId, this.selectedCertificateToReplace.id);
        }

        if (this.isFileUpdated) {
            fileUploadSuccess = await this._certificateRecordApiService.uploadCertificateFile(this.certificateRecord.id, this.uploadedCertificateFiles[0]);
        }

        if (success !== false || replaceSuccess !== false || (this.isFileUpdated && fileUploadSuccess)) {
            this.dialogRef.close(true);
        }
    }
}
